import { useEffect, useRef } from 'react'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { useOrgSwitch } from './OrgSwitchContext'
import { deriveOrgSwitchShellRole, ORG_SWITCH_MAX_WAIT_MS } from './orgSwitchConstants'
import {
  areOrgSwitchQueriesSettled,
  hasOrgSwitchQueriesWithData,
  matchesOrgSwitchQueryForPath
} from './orgSwitchQueryReadiness'

/**
 * Keeps the org-switch overlay visible through the post-`clear()` refetch storm
 * until queries for the **current route** have settled and returned data.
 */
export function useOrgSwitchReadyCoordinator(): void {
  const queryClient = useQueryClient()
  const { phase, targetOrgId, switchGeneration, endSwitch } = useOrgSwitch()
  const location = useLocation()
  const role = deriveOrgSwitchShellRole(location.pathname)
  const generationAtDataPhaseRef = useRef<number | null>(null)
  const pathnameRef = useRef(location.pathname)
  pathnameRef.current = location.pathname

  const targetOrgIdForQueries = phase === 'data' ? targetOrgId : null

  const isFetchingCritical = useIsFetching({
    predicate: (query) => {
      if (!targetOrgIdForQueries) return false
      return matchesOrgSwitchQueryForPath(query.queryKey, role, targetOrgIdForQueries, pathnameRef.current)
    }
  })

  useEffect(() => {
    if (phase !== 'data' || !targetOrgId) {
      generationAtDataPhaseRef.current = null
      return
    }
    generationAtDataPhaseRef.current = switchGeneration

    const timeoutId = window.setTimeout(() => {
      if (generationAtDataPhaseRef.current === switchGeneration) {
        endSwitch()
      }
    }, ORG_SWITCH_MAX_WAIT_MS)

    return () => window.clearTimeout(timeoutId)
  }, [phase, targetOrgId, switchGeneration, endSwitch])

  useEffect(() => {
    if (phase !== 'data' || !targetOrgId) return undefined
    if (generationAtDataPhaseRef.current !== switchGeneration) return undefined

    if (isFetchingCritical > 0) {
      return undefined
    }

    const pathname = pathnameRef.current
    const settled = areOrgSwitchQueriesSettled(queryClient, role, targetOrgId, pathname)
    const hasData = hasOrgSwitchQueriesWithData(queryClient, role, targetOrgId, pathname)

    if (!settled || !hasData) {
      return undefined
    }

    endSwitch()
    return undefined
  }, [phase, targetOrgId, switchGeneration, isFetchingCritical, queryClient, role, endSwitch])
}
